import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { fmtMDT } from "../lib/time";
import { cn } from "../lib/utils";
import {
  Laptop, Play, RefreshCw, Loader2, CheckCircle2, WifiOff, FileDown, ChevronRight, Mouse,
} from "lucide-react";
import PeripheralsPanel from "./Peripherals";

const SEV_STYLE = {
  critical: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  high:     "border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-300",
  medium:   "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  low:      "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  info:     "border-border bg-muted text-muted-foreground",
};
const SEV_ORDER = ["critical", "high", "medium", "low", "info"];

function SeverityPill({ sev }) {
  return (
    <span className={cn("inline-flex items-center rounded-full border text-[10.5px] font-semibold px-2 py-0.5 uppercase tracking-wide", SEV_STYLE[(sev || "info").toLowerCase()])}>
      {(sev || "info").toUpperCase()}
    </span>
  );
}

function KpiTile({ label, value, tone = "primary" }) {
  const toneCls = {
    primary: "text-foreground",
    rose: "text-rose-600 dark:text-rose-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
  }[tone];
  return (
    <div className="rounded-lg border border-border bg-background/60 p-2.5 min-w-[92px]">
      <div className={cn("text-[19px] font-semibold leading-none", toneCls)}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">{label}</div>
    </div>
  );
}

function DeviceDetailModal({ deviceId, onClose, onScan, scanning, toast, scanAvailable, isAdmin }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/vulnerability/workstations/${deviceId}`);
      setDetail(data);
    } catch {
      setDetail(null);
    } finally { setLoading(false); }
  }, [deviceId]);

  useEffect(() => { load(); }, [load]);

  const exportPdf = useCallback(async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`/vulnerability/workstations/${deviceId}/report`, { responseType: "blob" });
      const blob = new Blob([data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast?.({ title: "Export failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setExporting(false); }
  }, [deviceId, toast]);

  const device = detail?.device;
  const findings = detail?.findings || [];
  const isScanning = !!(scanning && scanning[deviceId]);

  return (
    <Dialog open={!!deviceId} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0" data-testid="workstation-detail-modal">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center justify-between gap-3 pr-6">
            <DialogTitle className="flex items-center gap-2 min-w-0">
              <Laptop className="w-4 h-4 text-primary shrink-0" />
              <span className="truncate">{device?.name || "Device"}</span>
            </DialogTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={exportPdf}
              disabled={exporting || !device}
              className="gap-1.5 shrink-0"
              data-testid="workstation-export-pdf-btn"
            >
              <FileDown className={cn("w-3.5 h-3.5", exporting && "animate-pulse")} />
              {exporting ? "Exporting…" : "Export to PDF"}
            </Button>
          </div>
          {device && (
            <DialogDescription>
              {[device.os, device.site, `last online ${fmtMDT(device.last_online)}`].filter(Boolean).join(" · ")}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && !detail ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <div key={i} className="h-14 rounded-md bg-muted/40 animate-pulse" />)}
            </div>
          ) : findings.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-muted-foreground">
              {device?.scan_status === "complete" ? "Clean — no findings on the last audit." : "Not scanned yet — run a scan to audit this endpoint."}
            </div>
          ) : (
            <ul className="space-y-1.5" data-testid="workstation-findings-list">
              {findings.map((f) => (
                <li key={f.id} className="rounded-lg border border-border bg-background/60 p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <SeverityPill sev={f.severity} />
                    <span className="text-[10px] rounded border border-border bg-muted/50 px-1.5 py-0.5 font-mono uppercase">{f.category}</span>
                    <span className="text-[13px] font-semibold min-w-0 truncate">{f.name}</span>
                  </div>
                  {f.description && (
                    <div className="text-[12px] text-muted-foreground mt-1 whitespace-pre-wrap">{f.description}</div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {device && (
            <div className="mt-4 pt-3 border-t border-border">
              <PeripheralsPanel deviceId={deviceId} isAdmin={isAdmin} embedded />
            </div>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t border-border">
          {isAdmin && scanAvailable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onScan?.(deviceId)}
              disabled={isScanning}
              className="gap-1.5 mr-auto"
              data-testid="workstation-scan-btn"
            >
              {isScanning ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning…</>) : (<><Play className="w-3.5 h-3.5" /> Run scan</>)}
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function WorkstationScans({ onModeChange }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "Super Admin" || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState({});
  const [scanAllBusy, setScanAllBusy] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [view, setView] = useState("devices");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/vulnerability/workstations/summary");
      setSummary(data);
      onModeChange?.(data?.mode);
      setScanning((prev) => {
        const next = {};
        for (const host of Object.keys(prev)) {
          const row = (data.devices || []).find((d) => d.id === host);
          if (row && row.scan_status === "running") next[host] = true;
        }
        return next;
      });
    } catch (e) {
      toast({ title: "Couldn't load workstations", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast, onModeChange]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const anyBusy = Object.keys(scanning).length > 0;
    if (!anyBusy) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [scanning, load]);

  const scanDevice = useCallback(async (deviceId) => {
    setScanning((s) => ({ ...s, [deviceId]: true }));
    try {
      await api.post(`/vulnerability/workstations/${deviceId}/scan`);
      toast({ title: "Scan started", description: `${deviceId} — endpoint audit running via NinjaOne.` });
      setTimeout(load, 3500);
    } catch (e) {
      toast({ title: "Couldn't start scan", description: e?.response?.data?.detail || "", variant: "destructive" });
      setScanning((s) => { const n = { ...s }; delete n[deviceId]; return n; });
    }
  }, [toast, load]);

  const sweepStartedRef = React.useRef(false);

  useEffect(() => {
    if (scanAllBusy && sweepStartedRef.current && Object.keys(scanning).length === 0) {
      setScanAllBusy(false);
      sweepStartedRef.current = false;
    }
  }, [scanAllBusy, scanning]);

  const scanAll = useCallback(async () => {
    setScanAllBusy(true);
    sweepStartedRef.current = false;
    try {
      const { data } = await api.post("/vulnerability/workstations/scan-all");
      const ids = data?.devices || [];
      if (ids.length === 0) {
        toast({ title: "Nothing to scan", description: "Every device already has a scan running or on record." });
        setScanAllBusy(false);
        return;
      }
      setScanning((s) => {
        const next = { ...s };
        for (const id of ids) next[id] = true;
        return next;
      });
      sweepStartedRef.current = true;
      // Safety net so the busy state can't get stuck forever.
      setTimeout(() => setScanAllBusy(false), 5 * 60 * 1000);
      toast({ title: "Fleet-wide sweep queued", description: `${ids.length} devices — running endpoint audits.` });
      setTimeout(load, 3500);
    } catch (e) {
      toast({ title: "Couldn't start sweep", description: e?.response?.data?.detail || "", variant: "destructive" });
      setScanAllBusy(false);
    }
  }, [toast, load]);

  const devices = summary?.devices || [];
  const isDemo = summary?.mode === "demo";
  const scanAvailable = summary?.scan_available !== false;

  return (
    <section className="rounded-xl border border-border bg-card" data-testid="workstation-scans-section">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Laptop className="w-4 h-4 text-primary shrink-0" />
          <div className="text-[13.5px] font-semibold">Office workstations</div>
          <span className={cn(
            "text-[10px] rounded-full border px-1.5 py-0.5 font-semibold uppercase tracking-wide",
            isDemo ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
          )} data-testid="workstation-mode-badge">
            {isDemo ? "Demo data" : "Live · NinjaOne"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-8 gap-1.5" data-testid="workstation-refresh-btn">
            <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} /> Refresh
          </Button>
          {isAdmin && (
            <Button
              size="sm"
              onClick={scanAll}
              disabled={scanAllBusy || !scanAvailable}
              title={!scanAvailable ? "Publish the endpoint-audit script(s) in NinjaOne and set NINJAONE_SCAN_SCRIPT_ID_WINDOWS / NINJAONE_SCAN_SCRIPT_ID_MAC to enable scanning" : undefined}
              className="h-8 gap-1.5"
              data-testid="workstation-scan-all-btn"
            >
              {scanAllBusy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning fleet…</>) : (<><Play className="w-3.5 h-3.5" /> Scan all workstations</>)}
            </Button>
          )}
        </div>
      </div>

      {isDemo ? (
        <div className="px-4 py-2 text-[11.5px] text-muted-foreground bg-amber-500/5 border-b border-border">
          Showing a modelled UnitedCloud office fleet. Wire real NinjaOne API credentials (Administration → Apps → API) into <code className="font-mono">NINJAONE_CLIENT_ID</code> / <code className="font-mono">NINJAONE_CLIENT_SECRET</code> to switch to live devices — no UI changes needed.
        </div>
      ) : !scanAvailable && (
        <div className="px-4 py-2 text-[11.5px] text-muted-foreground bg-amber-500/5 border-b border-border" data-testid="workstation-scan-unavailable-banner">
          Device inventory is live from NinjaOne ({devices.length} devices). Scanning is disabled until an endpoint-audit script is published in NinjaOne Automation and <code className="font-mono">NINJAONE_SCAN_SCRIPT_ID_WINDOWS</code> / <code className="font-mono">NINJAONE_SCAN_SCRIPT_ID_MAC</code> is set.
        </div>
      )}

      <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <KpiTile label="Devices" value={summary?.kpis?.total_devices ?? "—"} />
        <KpiTile label="Scanned" value={summary?.kpis?.scanned ?? "—"} tone="emerald" />
        <KpiTile label="Offline" value={summary?.kpis?.offline ?? "—"} />
        <KpiTile label="Crit / high" value={summary?.kpis?.open_critical_high ?? "—"} tone={(summary?.kpis?.open_critical_high || 0) > 0 ? "rose" : "emerald"} />
      </div>

      <div className="px-4 pt-3 flex items-center gap-1">
        <button
          type="button"
          onClick={() => setView("devices")}
          data-testid="workstation-view-toggle-devices"
          className={cn("inline-flex items-center gap-1.5 text-[11px] rounded-full border px-2.5 py-1 font-semibold uppercase tracking-wide",
            view === "devices" ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted/50")}
        >
          <Laptop className="w-3 h-3" /> Devices
        </button>
        <button
          type="button"
          onClick={() => setView("peripherals")}
          data-testid="workstation-view-toggle-peripherals"
          className={cn("inline-flex items-center gap-1.5 text-[11px] rounded-full border px-2.5 py-1 font-semibold uppercase tracking-wide",
            view === "peripherals" ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted/50")}
        >
          <Mouse className="w-3 h-3" /> Peripherals
        </button>
      </div>

      {view === "peripherals" ? (
        <div className="p-4">
          <PeripheralsPanel isAdmin={isAdmin} />
        </div>
      ) : (
      <div className="px-4 pb-4 pt-3">
        {loading && devices.length === 0 ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => <div key={i} className="h-14 rounded-md bg-muted/40 animate-pulse" />)}
          </div>
        ) : (
          <ul className="space-y-1.5 max-h-[520px] overflow-y-auto" data-testid="workstation-device-list">
            {devices.map((d) => {
              const worstSev = SEV_ORDER.find((k) => (d.severity_counts?.[k] || 0) > 0);
              const isScanning = !!scanning[d.id];
              return (
                <li key={d.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setDetailId(d.id)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailId(d.id); } }}
                    data-testid={`workstation-row-${d.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-border bg-background/60 px-3 py-2.5 w-full text-left transition-colors hover:bg-primary/5 hover:border-primary/30 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      {d.offline ? <WifiOff className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium truncate">{d.name}</div>
                        <div className="text-[10.5px] text-muted-foreground truncate">{[d.os, d.site].filter(Boolean).join(" · ")}{d.offline ? " · offline" : ""}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isScanning ? (
                        <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-sky-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning</span>
                      ) : d.scan_status === "complete" ? (
                        worstSev ? <SeverityPill sev={worstSev} /> : <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-emerald-500"><CheckCircle2 className="w-3.5 h-3.5" /> Clean</span>
                      ) : (
                        <span className="text-[10.5px] text-muted-foreground uppercase tracking-wide">Not scanned</span>
                      )}
                      {isAdmin && !isScanning && scanAvailable && d.scan_supported !== false && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); scanDevice(d.id); }}
                          data-testid={`workstation-scan-row-${d.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 text-primary px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide hover:bg-primary/20"
                        >
                          <Play className="w-3 h-3" /> Scan
                        </button>
                      )}
                      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      )}

      <DeviceDetailModal
        deviceId={detailId}
        onClose={() => setDetailId(null)}
        onScan={scanDevice}
        scanning={scanning}
        toast={toast}
        scanAvailable={scanAvailable}
        isAdmin={isAdmin}
      />
    </section>
  );
}
